import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useConfirm } from '@/contexts/confirmContext';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { platformAdminApi } from '../api/platformAdminApi';
import type { PlatformUser } from '../types/platformAdmin.types';

export function UsersPage() {
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const currentUser = useAppSelector(selectCurrentUser);
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter(
      user =>
        user.email.toLowerCase().includes(query) ||
        (user.tenantName || '').toLowerCase().includes(query)
    );
  }, [users, search]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setUsers(await platformAdminApi.listUsers());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleDeactivate = async (user: PlatformUser) => {
    const accepted = await confirm({
      title: 'Desactivar usuario',
      message: `¿Desactivar ${user.email}?`,
      confirmText: 'Desactivar',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!accepted) return;
    try {
      await platformAdminApi.deleteUser(user.id);
      toast.success('Usuario desactivado');
      await loadUsers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al desactivar usuario');
    }
  };

  return (
    <div className='container mx-auto py-6 space-y-6'>
      <Card>
        <CardHeader className='flex flex-row items-center justify-between'>
          <div>
            <CardTitle>Usuarios</CardTitle>
            <CardDescription>Administra roles y pertenencia a tenants.</CardDescription>
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={loadUsers} disabled={loading}>
              <RefreshCw className='h-4 w-4 mr-2' />
              Actualizar
            </Button>
            <Button onClick={() => navigate('/admin/users/new')}>
              <Plus className='h-4 w-4 mr-2' />
              Nuevo Usuario
            </Button>
          </div>
        </CardHeader>
        <CardContent className='space-y-4'>
          <Input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder='Buscar por email o tenant...'
            className='max-w-md'
          />

          <div className='border rounded-lg overflow-hidden'>
            <table className='w-full text-sm'>
              <thead className='bg-muted/50'>
                <tr>
                  <th className='text-left p-3'>Email</th>
                  <th className='text-left p-3'>Rol</th>
                  <th className='text-left p-3'>Tenant</th>
                  <th className='text-left p-3'>Estado</th>
                  <th className='text-right p-3'>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id} className='border-t'>
                    <td className='p-3 font-medium'>{user.email}</td>
                    <td className='p-3'>{user.role}</td>
                    <td className='p-3'>{user.tenantName || 'Sin tenant'}</td>
                    <td className='p-3'>{user.status === 'ACTIVE' ? 'Activo' : 'Inactivo'}</td>
                    <td className='p-3'>
                      <div className='flex justify-end gap-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant='destructive'
                          size='sm'
                          disabled={currentUser?.id === user.id}
                          onClick={() => handleDeactivate(user)}
                        >
                          Desactivar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={5} className='p-6 text-center text-muted-foreground'>
                      No hay usuarios para mostrar.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
